/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { NextUp, NextUpV3 } from "@packages/sdk";
import { getURLFromDeeplink } from "@packages/sdk";

import { spacing } from "../../../../../../global/emotion";
import type { ContainerProps } from "../../../../blocks/layout/Container/Container.emotion";
import { Container } from "../../../../blocks/layout/Container/Container.emotion";
import { Stack } from "../../../../blocks/layout/Stack/Stack.emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { SubscriptionIcon, VideoIcon } from "../../../icons/Icon/GeneralIcons";
import { InlineIcon } from "../../../icons/Icon/Icon";
import { PlayIcon } from "../../../icons/Icon/MediaIcons";
import type { NextUpLargeProps } from "../../nextUp/NextUpLarge/NextUpLarge.emotion";
import { NextUpLarge } from "../../nextUp/NextUpLarge/NextUpLarge.emotion";
import type {
  BaseGetSectionItemProps,
  SectionProps,
} from "../Section/Section.emotion";
import { SectionCarousel } from "../Section/SectionCarousel/SectionCarousel.emotion";

/**
 * TODO: MediaPlayer
 */
export type GetNextUpPropsProps = Omit<
  BaseGetSectionItemProps,
  "item" | "section"
> & {
  item: NextUp;
  index: number;
};

export const getNextUpProps = ({
  item,
  t,
  analytics,
  index,
}: GetNextUpPropsProps): NextUpLargeProps => {
  const url = getURLFromDeeplink({ deeplink: item.deeplink });

  const actions = !!url
    ? [
        <Button
          asChild
          key="button"
          startIcon={
            !item.has_access ? <SubscriptionIcon.Filled /> : <PlayIcon.Small />
          }
          variant="white"
          onClick={() => {
            analytics.track({
              event: HallowAnalyticsEvent.TappedNextUp,
              properties: {
                index,
                deeplink: item.deeplink,
                reason_code: item.reason_code,
                tag: item.reason_label,
                title: item.title,
              },
            });
            analytics.track({
              event: item.has_access
                ? HallowAnalyticsEvent.TappedPlaySession
                : item.has_video
                  ? HallowAnalyticsEvent.TappedSubscribeToWatch
                  : HallowAnalyticsEvent.TappedSubscribeToListen,
              properties: {
                screen_name: "homepage",
                section_item_id: "nextup",
              },
            });
          }}
        >
          <Link href={url}>
            {t(
              item.has_access
                ? "play_session"
                : item?.has_video
                  ? "button_subscribe_to_watch"
                  : "button_subscribe_to_listen",
            )}
          </Link>
        </Button>,
      ]
    : [];

  const description = (
    <>
      {item.has_video ? (
        <InlineIcon icon={<VideoIcon.Filled css={inlineIconStyle} />} />
      ) : null}
      {item.description}
    </>
  );

  return {
    actions,
    color: (item.color_hex ??
      item.images?.color_hex) as NextUpLargeProps["color"],
    description,
    imgSrc: item.images ?? { large: item.image_url },
    subscription: !item.has_access,
    tag: item.reason_label,
    title: item.title,
  };
};

const paddingBottomStyle: Interpolation = {
  paddingBottom: spacing.m,
};
const paddingTopStyle: Interpolation = {
  paddingTop: spacing.m,
};
const inlineIconStyle: Interpolation = {
  marginRight: spacing.xs,
};

export type SectionNextUpProps = ContainerProps &
  Omit<SectionProps, "section"> & {
    nextUp?: NextUpV3;
  };

export type SingleNextUpProps = Omit<
  SectionNextUpProps,
  "nextUp" | "section"
> & {
  nextUp?: NextUp;
  index: number;
};

const SingleNextUp = ({
  link = true,
  mutate = true,
  nextUp,
  index,
}: SingleNextUpProps) => {
  const analytics = useAnalytics();
  const t = useTranslations();

  return (
    <NextUpLarge
      {...getNextUpProps({ item: nextUp, link, mutate, t, analytics, index })}
    />
  );
};

export const SectionNextUp = ({
  link = true,
  mutate = true,
  nextUp,
  ...props
}: SectionNextUpProps) => {
  const t = useTranslations();

  const supportedNextUpSuggestions = nextUp.suggestions.filter(
    (item) =>
      item.reason_code !== "community_challenge" &&
      !item.deeplink.includes("/community/"),
  );

  if (supportedNextUpSuggestions.length === 0) {
    return null;
  }

  return (
    <Stack {...props}>
      <Container css={[paddingBottomStyle, paddingTopStyle]}>
        <Container paddingX>
          <Text as="h2" overflow="ellipsis" size="m" type="headline">
            {t("home_up_next")}
          </Text>
        </Container>
        {supportedNextUpSuggestions.length > 1 ? (
          <SectionCarousel size={"xl"}>
            {supportedNextUpSuggestions.map((item, idx) => (
              <SingleNextUp
                link={link}
                mutate={mutate}
                nextUp={item}
                index={idx}
              />
            ))}
          </SectionCarousel>
        ) : (
          <Container paddingX paddingY>
            <SingleNextUp
              link={link}
              mutate={mutate}
              nextUp={supportedNextUpSuggestions[0]}
              index={0}
            />
          </Container>
        )}
      </Container>
    </Stack>
  );
};
